// src/components/MembersPage.js
import React, { useEffect, useState, useMemo, useRef } from "react";
import { Search, Plus, FilePenLine, Trash2, MoreHorizontal, ChevronLeft, ChevronRight, ArrowUpDown, X, Download } from 'lucide-react';
import MemberForm from "./MemberForm";
import OfflineBanner from "./OfflineBanner";
import useOfflineSync from "../hooks/useOfflineSync";

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';
// Derive the public path for uploaded attachments (stored in ../self_registered_attachments)
const ATTACH_BASE_URL = API_BASE_URL.replace(/\/api$/, '') + '/self_registered_attachments';

const disabilityTypes = [
  "ዓይነ ስውር",
  "መስማት የተሳነው",
  "የአካል / የመንቀሳቀስ እክል",
  "የአዕምሮ እድገት ውስንነት",
  "የአእምሮ ጤና እክል",
  "ሥር የሰደደ ሕመም",
  "የመናገር እክል",
  "ኦቲዝም/የነርቭ ስርዓት እክል",
  "የተለየ",
];

const genders = ["ወንድ", "ሴት"];
const maritalStatuses = ["ያላገባ/ያላገባች", "ያገባ/ያገባች", "የፈታ/የፈታች", "መበለት"];
const educationLevels = [
  "መደበኛ ትምህርት ያሎሰደ",
              "አንደኛ ደረጃ",
              "ሁለተኛ ደረጃ",
              "ዲፕሎማ",
              "የመጀመሪያ ዲግሪ",
              "ማስተርስ",
              "ዶክትሬት",
              "ሌላ",
];

export default function MembersPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [detailMember, setDetailMember] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [opdSubCity, setOpdSubCity] = useState("");
  const [filters, setFilters] = useState({
    disabilityType: "",
    gender: "",
    woreda: "",
    educationLevel: "",
    maritalStatus: "",
    labourID: "",
    supportNeeded: "",
    workNeeded: "",
    ageMin: "",
    ageMax: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const formModalRef = useRef(null);
  const detailModalRef = useRef(null);
  const liveRegionRef = useRef(null);

  // Extract current user email from localStorage
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const currentUserEmail = currentUser?.email;

  const fetchMembers = async () => {
    setLoading(true);
    try {
      if (!currentUserEmail) {
        alert("Please log in to view members.");
        setLoading(false);
        return;
      }
      // Fetch OPD profile to get sub city
      let subCity = opdSubCity;
      if (!subCity) {
        const profRes = await fetch(`${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(currentUserEmail)}`);
        const profData = await profRes.json();
        if (profData?.status === 'success' && profData?.data?.sub_city) {
          subCity = profData.data.sub_city;
          setOpdSubCity(subCity);
        } else {
          // If profile missing, stop early
          setMembers([]);
          setLoading(false);
          return;
        }
      }

      // Build query parameters (no email; fetch all then filter by sub city)
      const params = new URLSearchParams({
        ...(searchTerm && { search: searchTerm }),
        ...(filters.disabilityType && { disabilityType: filters.disabilityType }),
        ...(filters.gender && { gender: filters.gender }),
        ...(filters.woreda && { woreda: filters.woreda }),
        ...(filters.educationLevel && { educationLevel: filters.educationLevel }),
        ...(filters.maritalStatus && { maritalStatus: filters.maritalStatus }),
        ...(filters.labourID && { labourID: filters.labourID }),
        ...(filters.supportNeeded && { supportNeeded: filters.supportNeeded }),
        ...(filters.workNeeded && { workNeeded: filters.workNeeded }),
        ...(filters.ageMin && { ageMin: filters.ageMin }),
        ...(filters.ageMax && { ageMax: filters.ageMax })
      });

      const response = await fetch(
        `${API_BASE_URL}/getAllMembers.php?${params.toString()}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        const norm = (s) => (s || '').toString().trim().toLowerCase();
        const subCityNorm = norm(subCity);
        const bySubCity = data.filter(m => norm(m.subCity) === subCityNorm);
        setMembers(bySubCity);
      } else {
        console.error("Invalid response format:", data);
        setMembers([]);
      }
    } catch (error) {
      console.error("Failed to fetch members:", error);
      alert("Failed to fetch members.");
      setMembers([]);
    }
    setLoading(false);
  };

  // Offline sync functionality
  const {
    offlineMembers,
    syncStatus,
    syncMessage,
    syncOfflineMembers,
    hasOfflineMembers,
    clearFailedFromLastSync
  } = useOfflineSync(fetchMembers);

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const diffMs = Date.now() - birthDate.getTime();
    const ageDt = new Date(diffMs);
    return Math.abs(ageDt.getUTCFullYear() - 1970);
  };

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const onMemberAdded = () => {
    fetchMembers();
    setEditingMember(null);
  };

  // Handle banner dismiss
  const handleBannerDismiss = () => {
    // Banner will auto-hide based on sync status
  };

  // Combine database members with offline members for display
  const allMembers = useMemo(() => {
    const combinedMembers = [...members];
    
    // Add offline members to the display
    offlineMembers.forEach(offlineMember => {
      combinedMembers.push({
        ...offlineMember,
        id: offlineMember.tempId, // Use tempId as id for display
        isOfflinePending: true
      });
    });
    
    return combinedMembers;
  }, [members, offlineMembers]);

  const filteredMembers = useMemo(() => {
    return allMembers.filter((member) => {
      const fullName = `${member.firstName} ${member.middleName || ""} ${member.lastName}`.toLowerCase();

      if (
        searchTerm &&
        !fullName.includes(searchTerm.toLowerCase()) &&
        !((member.idNumber || member.uniqueID || "").toString().toLowerCase().includes(searchTerm.toLowerCase()))
      ) {
        return false;
      }
      // Only show approved members
      if ((member.aaapd_status || "").toString().toLowerCase() !== "approved") return false;
      
      // DisabilityType: support comma-separated multiple values in DB
      if (filters.disabilityType) {
        const target = filters.disabilityType.toLowerCase();
        const memberTypes = (member.disabilityType || "")
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
        if (!memberTypes.includes(target)) return false;
      }
      if (filters.gender && member.gender !== filters.gender) return false;
      if (filters.woreda && (member.woreda || "").toLowerCase() !== filters.woreda.toLowerCase()) return false;
      if (filters.educationLevel && member.educationLevel !== filters.educationLevel) return false;
      if (filters.maritalStatus && member.maritalStatus !== filters.maritalStatus) return false;
      if (filters.labourID && member.labourID !== filters.labourID) return false;
      if (filters.supportNeeded && !member.supportNeeded?.toLowerCase().includes(filters.supportNeeded.toLowerCase())) return false;
      if (filters.workNeeded && !member.workNeeded?.toLowerCase().includes(filters.workNeeded.toLowerCase())) return false;

      // Use explicit age field from the form; fallback to DOB-based age if missing/invalid
      const ageNum = Number(member.age);
      const effectiveAge = (!Number.isNaN(ageNum) && ageNum > 0) ? ageNum : getAge(member.dateOfBirth);
      if (filters.ageMin && effectiveAge < Number(filters.ageMin)) return false;
      if (filters.ageMax && effectiveAge > Number(filters.ageMax)) return false;

      return true;
    });
  }, [allMembers, searchTerm, filters]);

  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage) || 1;
  const paginatedMembers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredMembers.slice(start, start + itemsPerPage);
  }, [filteredMembers, currentPage]);

  // Function to export filtered members to CSV
  const exportToCSV = () => {
    if (filteredMembers.length === 0) {
      alert('No members to export');
      return;
    }

    // Comprehensive headers (removed 'ID' as requested)
    const headers = [
      'Full Name',
      'Gender',
      'Date of Birth',
      'Age',
      'Phone Number',
      'Alternative Phone Number',
      'Email',
      'Location',
      'Specific Location',
      'Sub City',
      'Woreda',
      'House Number',
      'Disability Type',
      'Disability Other',
      'Disability Description',
      'Education Level',
      'Marital Status',
      'Number of Families',
      'Employment Status',
      'Support Needed',
      'Work Needed',
      'Support Received',
      'Place of Work',
      'Name of the Office',
      'Type of the Work',
      'Job Responsibilities',
      'Labour ID',
      'Labour ID Number',
      'Membership Status',
      'ID Type',
      'ID Number',
      'Unique ID',
      'Passport Photo URL',
      'Receipt/ID Attachment URL',
    ];

    const escapeCsv = (v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;

    const rows = filteredMembers.map((m) => {
      const fullName = [m.firstName, m.middleName, m.lastName].filter(Boolean).join(' ');
      const photoUrl = m.photoAttachment ? `${ATTACH_BASE_URL}/${m.photoAttachment}` : '';
      const idAttachUrl = m.idAttachment ? `${ATTACH_BASE_URL}/${m.idAttachment}` : '';
      const disabilityType = m.disabilityType === 'Other' ? (m.disabilityOther || 'Other') : (m.disabilityType || '');
      return [
        fullName,
        m.gender || '',
        m.dateOfBirth || '',
        m.age || '',
        m.phoneNumber || '',
        m.AltPhoneNumber || '',
        m.email || '',
        m.location || '',
        m.specificLocation || '',
        m.subCity || '',
        m.woreda || '',
        m.houseNumber || '',
        disabilityType,
        m.disabilityOther || '',
        m.disabilityDescription || '',
        m.educationLevel || '',
        m.maritalStatus || '',
        m.numberOfFamilies || '',
        m.employmentStatus || '',
        m.supportNeeded || '',
        m.workNeeded || '',
        m.supportReceived || '',
        m.workPlace || '',
        m.officeName || '',
        m.workType || '',
        m.jobResponsibility || '',
        m.labourID || '',
        m.labourIDNumber || '',
        m.aaapd_status || '',
        m.idType || '',
        m.idNumber || '',
        m.uniqueID || '',
        photoUrl,
        idAttachUrl,
      ];
    });

    const csv = [headers, ...rows]
      .map((r) => r.map(escapeCsv).join(','))
      .join('\n');

    // Prepend BOM for correct Amharic display in Excel
    const csvWithBom = '\uFEFF' + csv;
    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.setAttribute('href', url);
    link.setAttribute('download', `members_export_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;
    
    try {
      const user = JSON.parse(localStorage.getItem("currentUser"));
      if (!user || !user.email) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/deleteMember.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: id,
          requestEmail: user.email
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        fetchMembers(); // Refresh the member list
        alert("Member deleted successfully and moved to Trash");
      } else {
        throw new Error(data.message || 'Failed to delete member');
      }
    } catch (error) {
      console.error("Error deleting member:", error);
      alert(error.message || "Failed to delete member");
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setShowForm(true);
  };

  const handleDetail = (member) => {
    setDetailMember(member);
    setShowDetails(true);
  };

  const closeDetailModal = () => {
    setShowDetails(false);
    setDetailMember(null);
  };

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
  };

  const onFormClose = () => {
    setShowForm(false);
    setEditingMember(null);
  };

  // Focus trap and escape support
  const trapFocus = (modalRef, onClose, title) => {
    const focusableElementsString = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]';
    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = Array.from(modal.querySelectorAll(focusableElementsString));
    if (focusableElements.length === 0) return;

    focusableElements[0].focus();

    // Announce modal to screen reader
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = `${title} opened`;
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
      if (e.key === "Tab") {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    modal.addEventListener("keydown", handleKeyDown);
    return () => modal.removeEventListener("keydown", handleKeyDown);
  };

  useEffect(() => {
    if (showForm) {
      return trapFocus(formModalRef, onFormClose, "Add/Edit Member Form");
    }
  }, [showForm]);

  useEffect(() => {
    if (showDetails) {
      return trapFocus(detailModalRef, closeDetailModal, "Member Details");
    }
  }, [showDetails]);

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">ሁሉም አባላት/All Members</h1>
          <p className="text-sm text-gray-500 mt-1">አባላትን አስተዳድር/Manage members</p>
        </header>

        {/* Offline Banner */}
        <OfflineBanner
          syncStatus={syncStatus}
          syncMessage={syncMessage}
          pendingCount={offlineMembers.length}
          onSync={syncOfflineMembers}
          onDismiss={handleBannerDismiss}
          onClearFailed={clearFailedFromLastSync}
        />

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex-1 flex items-center space-x-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search members (with name & their ID)"
                className="pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search members"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}

        {/* Attachment Lightbox Modal */}
        {attachmentPreview && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
            <div className="relative max-w-5xl max-h-[90vh] w-auto m-4">
              <button
                type="button"
                className="absolute -top-3 -right-3 bg-white text-gray-700 rounded-full shadow p-2 hover:bg-gray-100"
                onClick={() => setAttachmentPreview(null)}
                aria-label="Close attachment preview"
              >
                <X size={20} />
              </button>
              <img
                src={attachmentPreview}
                alt="Attachment Preview"
                className="max-h-[85vh] max-w-full rounded-lg shadow-2xl"
              />
            </div>
          </div>
        )}
            </div>
            <div className="hidden md:flex items-center ml-2" aria-live="polite">
              <span className="px-3 py-1 rounded-full bg-teal-50 text-teal-700 font-semibold text-sm">
                {filteredMembers.length} result{filteredMembers.length !== 1 ? 's' : ''}
              </span>
            </div>
            <button
              onClick={exportToCSV}
              disabled={filteredMembers.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export to CSV"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={() => {
                setEditingMember(null);
                setShowForm(true);
              }}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white font-semibold rounded-lg shadow-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition"
              aria-label="Add new member"
            >
              <Plus size={20} />
              <span>አባል ጨምር/Add Member</span>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm px-4 py-3 mb-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Filter Members</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 items-end">
            <select
              name="disabilityType"
              value={filters.disabilityType}
              onChange={onFilterChange}
              className="w-full border-gray-300 rounded-lg shadow-sm text-sm px-3 py-1.5 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition"
              aria-label="Filter by Disability Type"
            >
              <option value="">ሁሉም የአካል ጉዳት ዓይነቶች</option>
              {disabilityTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            <select
              name="gender"
              value={filters.gender}
              onChange={onFilterChange}
              className="w-full border-gray-300 rounded-lg shadow-sm text-sm px-3 py-1.5 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition"
              aria-label="Filter by Gender"
            >
              <option value="">ሁለቱም ፆታዎች</option>
              {genders.map((gender) => (
                <option key={gender} value={gender}>{gender}</option>
              ))}
            </select>

            <input
              type="text"
              name="woreda"
              value={filters.woreda}
              onChange={onFilterChange}
              placeholder="በወረዳ አጣራ/Filter by Woreda"
              className="w-full border-gray-300 rounded-lg shadow-sm text-sm px-3 py-1.5 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition"
              aria-label="Filter by woreda"
            />

            <select
              name="educationLevel"
              value={filters.educationLevel}
              onChange={onFilterChange}
              className="w-full border-gray-300 rounded-lg shadow-sm text-sm px-3 py-1.5 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition"
              aria-label="Filter by Education Level"
            >
              <option value="">ሁሉም የትምህርት ደረጃዎች</option>
              {educationLevels.map((level) => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>

            <select
              name="maritalStatus"
              value={filters.maritalStatus}
              onChange={onFilterChange}
              className="w-full border-gray-300 rounded-lg shadow-sm text-sm px-3 py-1.5 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition"
              aria-label="Filter by Marital Status"
            >
              <option value="">ሁሉም የጋብቻ ሁኔታዎች</option>
              {maritalStatuses.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>

            <select
              name="labourID"
              value={filters.labourID}
              onChange={onFilterChange}
              className="w-full border-gray-300 rounded-lg shadow-sm text-sm px-3 py-1.5 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition"
              aria-label="Filter by Labour ID"
            >
              <option value="">ሁሉም የሠራተኛ መለያ ቁጥር</option>
              <option value="ያለው">ያለው</option>
              <option value="የሌለው">የሌለው</option>
            </select>

            <input
              type="text"
              name="supportNeeded"
              value={filters.supportNeeded}
              onChange={onFilterChange}
              placeholder="በሚያስፈልገው ድጋፍ አጣራ/Filter by Support Needed"
              className="w-full border-gray-300 rounded-lg shadow-sm text-sm px-3 py-1.5 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition"
              aria-label="Filter by Support Needed"
            />
            <input
              type="text"
              name="workNeeded"
              value={filters.workNeeded}
              onChange={onFilterChange}
              placeholder="በሚፈለገው ሥራ አጣራ/Filter by Work Needed"
              className="w-full border-gray-300 rounded-lg shadow-sm text-sm px-3 py-1.5 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition"
              aria-label="Filter by Work Needed"
            />

            <input
              type="number"
              name="ageMin"
              value={filters.ageMin}
              onChange={onFilterChange}
              placeholder="ዝቅተኛ ዕድሜ/Min Age"
              className="w-full border-gray-300 rounded-lg shadow-sm text-sm px-3 py-1.5 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition"
              min={0}
              aria-label="Minimum Age Filter"
            />

            <input
              type="number"
              name="ageMax"
              value={filters.ageMax}
              onChange={onFilterChange}
              placeholder="ከፍተኛው ዕድሜ/Max Age"
              className="w-full border-gray-300 rounded-lg shadow-sm text-sm px-3 py-1.5 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition"
              min={0}
              aria-label="Maximum Age Filter"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading members...</p>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-700">ምንም አባላት አልተገኙም።/No Members Found</h3>
            <p className="text-gray-500 mt-1">አሁን ካለው መስፈርት ጋር የሚዛመዱ አባላት የሉም።/No members match the current criteria.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Table for larger screens */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider w-12">#</th>
                    <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">ሙሉ ስም/Full Name</th>
                    <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">እድሜ/Age</th>
                    <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">የአካል ጉዳት አይነት/Disability Type</th>
                    <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">ጾታ/Gender</th>
                    <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">ID Number</th>
                    <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">Membership Status</th>
                    <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">Status</th>
                    <th className="py-3 px-6 text-center font-semibold text-gray-600 text-sm uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedMembers.map((member, index) => (
                    <tr key={member.id} className={`hover:bg-gray-50 transition ${member.isOfflinePending ? 'bg-orange-50' : ''}`}>
                      <td className="py-4 px-4 whitespace-nowrap text-gray-600">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td className="py-4 px-6 whitespace-nowrap">{`${member.firstName} ${member.middleName ? member.middleName + " " : ""}${member.lastName}`}</td>
                      <td className="py-4 px-6 whitespace-nowrap">{member.age}</td>
                      <td className="py-4 px-6 whitespace-nowrap">{member.disabilityType === "Other" ? member.disabilityOther || "Other" : member.disabilityType}</td>
                      <td className="py-4 px-6 whitespace-nowrap">{member.gender}</td>
                      <td className="py-4 px-6 whitespace-nowrap">{member.uniqueID || '-'}</td>
                      <td className="py-4 px-6 whitespace-nowrap">
                        {(() => {
                          const v = (member.aaapd_status || '').toString();
                          const s = v.toLowerCase();
                          const cls = s === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : s === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : s === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800';
                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                              {v || '-'}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-4 px-6 whitespace-nowrap">
                        {member.isOfflinePending ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Pending Sync
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Synced
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-6 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button 
                            onClick={() => handleDetail(member)} 
                            className="text-gray-500 hover:text-teal-600 transition" 
                            title="View Details"
                            disabled={member.isOfflinePending}
                          >
                            <MoreHorizontal size={20} />
                          </button>
                          <button 
                            onClick={() => handleEdit(member)} 
                            className="text-gray-500 hover:text-blue-600 transition" 
                            title="Edit Member"
                            disabled={member.isOfflinePending}
                          >
                            <FilePenLine size={20} />
                          </button>
                          <button 
                            onClick={() => handleDelete(member.id)} 
                            className="text-gray-500 hover:text-red-600 transition" 
                            title="Delete Member"
                            disabled={member.isOfflinePending}
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards for smaller screens */}
            <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
              {paginatedMembers.map((member) => (
                <div key={member.id} className={`bg-white border border-gray-200 rounded-lg shadow-sm p-4 flex flex-col justify-between ${member.isOfflinePending ? 'border-orange-200 bg-orange-50' : ''}`}>
                  <div>
                    <h4 className="font-bold text-lg text-gray-800">{`${member.firstName} ${member.middleName}`}</h4>
                    <p className="text-sm text-gray-600">ID: {member.idNumber}</p>
                    <p className="text-sm text-gray-600">{member.disabilityType}</p>
                    {member.isOfflinePending && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 mt-2">
                        Pending Sync
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-4">
                    <button 
                      onClick={() => handleDetail(member)} 
                      className="text-gray-500 hover:text-teal-600 transition" 
                      title="View Details"
                      disabled={member.isOfflinePending}
                    >
                      <MoreHorizontal size={20} />
                    </button>
                    <button 
                      onClick={() => handleEdit(member)} 
                      className="text-gray-500 hover:text-blue-600 transition" 
                      title="Edit Member"
                      disabled={member.isOfflinePending}
                    >
                      <FilePenLine size={20} />
                    </button>
                    <button 
                      onClick={() => handleDelete(member.id)} 
                      className="text-gray-500 hover:text-red-600 transition" 
                      title="Delete Member"
                      disabled={member.isOfflinePending}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && filteredMembers.length > 0 && (
          <div className="mt-4 flex flex-col md:flex-row justify-between items-center gap-3">
            <span className="text-sm text-gray-600">Showing {paginatedMembers.length} of {filteredMembers.length} members</span>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
              <button
                className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
        {/* Member Details Modal */}
        {showDetails && detailMember && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out" aria-modal="true" role="dialog">
            <div ref={detailModalRef} className="relative bg-gray-100 rounded-xl shadow-2xl w-full max-w-3xl m-4 max-h-[90vh] flex flex-col animate-fade-in-down">
              <div className="flex items-center justify-between p-5 border-b border-gray-200 rounded-t-xl bg-white">
                <h3 id="member-details-title" className="text-xl font-semibold text-gray-800">
                  Member Details
                </h3>
                <div className="flex items-center gap-3">
                  {detailMember.photoAttachment && (
                    isImageFile(detailMember.photoAttachment) ? (
                      <button
                        type="button"
                        className="group"
                        title="View passport photo"
                        onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`)}
                      >
                        <img
                          src={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                          alt="Passport Photo"
                          className="h-24 w-20 rounded object-cover object-top border border-gray-200 shadow-sm group-hover:opacity-90"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </button>
                    ) : (
                      <a
                        href={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-teal-600 hover:underline"
                        title="View passport photo"
                      >
                        Passport Photo
                      </a>
                    )
                  )}
                  <button
                    type="button"
                    className="p-2 text-gray-400 bg-transparent rounded-lg hover:bg-gray-200 hover:text-gray-900"
                    onClick={closeDetailModal}
                    aria-label="Close details"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="p-6 flex-grow overflow-y-auto">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[ 
                      { label: "Full Name", value: `${detailMember.firstName} ${detailMember.middleName || ""} ${detailMember.lastName}` },
                      { label: "Gender", value: detailMember.gender },
                      { label: "Date of Birth", value: detailMember.dateOfBirth },
                      { label: "Age", value: detailMember.age },
                      { label: "Phone Number", value: detailMember.phoneNumber },
                      { label: "Alternative Phone Number", value: detailMember.AltPhoneNumber },
                      { label: "Email", value: detailMember.email },
                      { label: "Location", value: detailMember.location },
                      { label: "Sub City", value: detailMember.subCity },
                      { label: "Woreda", value: detailMember.woreda },
                      { label: "House Number", value: detailMember.houseNumber },
                      { label: "Specific Location", value: detailMember.specificLocation },
                      { label: "Disability Type", value: detailMember.disabilityType === "Other" ? detailMember.disabilityOther || "Other" : detailMember.disabilityType },
                      { label: "Disability Description", value: detailMember.disabilityDescription },
                      { label: "Education Level", value: detailMember.educationLevel },
                      { label: "Marital Status", value: detailMember.maritalStatus },
                      { label: "Number of Families", value: detailMember.numberOfFamilies },
                      { label: "Employment Status", value: detailMember.employmentStatus },
                      { label: "Member Status", value: detailMember.aaapd_status },
                      { label: "Labour ID", value: detailMember.labourID },
                      { label: "Labour ID Number", value: detailMember.labourIDNumber },
                      { label: "Place of Work", value: detailMember.workPlace },
                      { label: "Name of the Office", value: detailMember.officeName },
                      { label: "Type of the Work", value: detailMember.workType },
                      { label: "Job Responsibilities", value: detailMember.jobResponsibility },
                      { label: "Support Needed", value: detailMember.supportNeeded },
                      { label: "Work Needed", value: detailMember.workNeeded },
                      { label: "Support Received", value: detailMember.supportReceived },
                      { label: "ID Type", value: detailMember.idType },
                      { label: "ID Number", value: detailMember.idNumber },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <p className="text-sm font-medium text-gray-500">{label}</p>
                        {label === "Member Status" ? (
                          (() => {
                            const v = (value || "").toString();
                            const s = v.toLowerCase();
                            const cls = s === "approved"
                              ? "bg-green-100 text-green-800"
                              : s === "rejected"
                              ? "bg-red-100 text-red-800"
                              : s === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-800";
                            return (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                                {v || "-"}
                              </span>
                            );
                          })()
                        ) : (
                          <p className="text-base text-gray-800 font-semibold">{value || "-"}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* ID Attachment Preview */}
                  {detailMember.idAttachment && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-500 mb-2">Receipt Attachment</p>
                      {isImageFile(detailMember.idAttachment) ? (
                        <button
                          type="button"
                          className="group"
                          onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.idAttachment}`)}
                          title="Click to enlarge"
                        >
                          <img
                            src={`${ATTACH_BASE_URL}/${detailMember.idAttachment}`}
                            alt="ID Attachment"
                            className="max-h-64 rounded-lg border border-gray-200 shadow-sm group-hover:opacity-90"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        </button>
                      ) : (
                        <a
                          href={`${ATTACH_BASE_URL}/${detailMember.idAttachment}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:underline"
                        >
                          View/Download Attachment
                        </a>
                      )}
                    </div>
                  )}

                  {/* Passport Photo Preview */}
                  {detailMember.photoAttachment && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-500 mb-2">Passport Photo</p>
                      {isImageFile(detailMember.photoAttachment) ? (
                        <button
                          type="button"
                          className="group"
                          onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`)}
                          title="Click to enlarge"
                        >
                          <img
                            src={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                            alt="Passport Photo"
                            className="max-h-64 rounded-lg border border-gray-200 shadow-sm group-hover:opacity-90"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        </button>
                      ) : (
                        <a
                          href={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:underline"
                        >
                          View/Download Photo
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-white rounded-b-xl">
                <button
                  type="button"
                  onClick={closeDetailModal}
                  className="px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Member Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out" aria-modal="true" role="dialog">
            <div ref={formModalRef} className="relative bg-gray-100 rounded-xl shadow-2xl w-full max-w-2xl m-4 max-h-[90vh] flex flex-col animate-fade-in-down">
              <div className="flex items-center justify-between p-5 border-b border-gray-200 rounded-t-xl bg-white">
                <h3 id="member-form-title" className="text-xl font-semibold text-gray-800">
                  {editingMember ? 'Edit Member' : 'የአዲስ አበባ ከተማ አስተዳደር የአካል ጉዳተኞች ማኅበር መመዝገቢያ ቅጽ'}
                </h3>
                <button
                  type="button"
                  className="p-2 text-gray-400 bg-transparent rounded-lg hover:bg-gray-200 hover:text-gray-900"
                  onClick={onFormClose}
                  aria-label="Close form"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 flex-grow overflow-y-auto">
                <MemberForm
                  onMemberAdded={() => {
                    onMemberAdded();
                    setShowForm(false);
                  }}
                  onClose={onFormClose}
                  editingMember={editingMember}
                  currentUserEmail={currentUserEmail}
                />
              </div>
            </div>
          </div>
        )}

        {/* Screen reader live region */}
        <div ref={liveRegionRef} className="sr-only" aria-live="polite" aria-atomic="true" />
      </div>
    </div>
  );
}
